import React from "react";
import { House as Home, Wind, ClipboardCheck, Building2, Droplets, FileCheck, Video, HardHat, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const services = [{
  title: "Home Inspection",
  description: "Complete visual inspection of all major systems—roof, electrical, plumbing, HVAC, and foundation. For buyers, sellers, and homeowners.",
  icon: Home,
  badge: null,
  link: "/HomeInspectionServicePage"
}, {
  title: "Wind Mitigation",
  description: "Documents eligible wind-resistant features commonly reviewed by Florida insurance carriers. A wind mitigation inspection may help identify available insurance credits but does not guarantee discounts, coverage, or carrier approval.",
  icon: Wind,
  badge: "Insurance",
  link: "/WindMitigationInspectionPage"
}, {
  title: "4-Point Inspection",
  description: "Focused inspection of roof, electrical, plumbing, and HVAC—required by many Florida insurers for homes over 25 years old or when changing coverage.",
  icon: ClipboardCheck,
  badge: "Required",
  link: "/FourPointInspectionSouthFloridaPage"
}, {
  title: "Condo Inspection",
  description: "Interior-focused inspection of unit condition, fixtures, appliances, and accessible systems. For condo buyers and owners protecting their investment.",
  icon: Building2,
  badge: null,
  link: "/CondoInspectionPage"
}, {
  title: "Mold Testing",
  description: "Professional air and surface sampling to detect moisture and mold. Critical in South Florida's humid climate before closing or after water events.",
  icon: Droplets,
  badge: null,
  link: "/MoldTestingPage"
}, {
  title: "Pre-Listing Inspection",
  description: "Proactive assessment for sellers to identify roof issues, plumbing problems, HVAC concerns, or structural issues before listing on a competitive market.",
  icon: FileCheck,
  badge: null,
  link: "/PreListingInspectionPage"
}, {
  title: "Sewer Camera Inspection",
  description: "High-definition video inspection of underground sewer lines to identify blockages, root intrusion, or pipe damage before they become costly emergencies.",
  icon: Video,
  badge: "New",
  link: "/SewerCameraInspectionPage"
}, {
  title: "New Construction & Warranty",
  description: "Final walkthrough, pre-drywall and 11-month warranty inspections for newly built homes. A visual inspection of accessible components, not a code inspection or warranty.",
  icon: HardHat,
  badge: null,
  link: "https://trusted-fl.com/new-construction-inspections/"
}];
export default function ProfessionalPlumbingSolutions() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="7240" id="our-inspection-services" className="bg-background py-24 relative overflow-hidden" ref={ref}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-5xl font-extrabold text-secondary mb-4 uppercase tracking-tighter">
            Inspection Services in South Florida
          </h2>
          <p className="text-lg text-muted-foreground">
            Trusted Inspections provides buyer, seller, condo, insurance, and mold inspection services across South Florida.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-8">
          {services.map((service, index) => <div key={index} data-index={index} className={`transition-all duration-700 delay-[${index * 100}ms] ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              <Card className="group h-full border border-border bg-card hover:border-primary hover:-translate-y-2 transition-all duration-300 shadow-lg hover:shadow-xl relative overflow-hidden">
                <CardHeader className="pb-4">
                  <div className="flex justify-between items-start">
                    <div className="w-full aspect-video rounded-lg bg-muted flex items-center justify-center mb-4 overflow-hidden group-hover:bg-accent transition-colors duration-300">
                      <service.icon className="size-12 md:size-14 text-secondary group-hover:text-primary group-hover:scale-110 transition-all duration-500" strokeWidth={1.2} />
                    </div>
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <CardTitle className="text-[22px] font-bold text-secondary group-hover:text-primary transition-colors">
                      {service.title}
                    </CardTitle>
                    {service.badge && <Badge variant="secondary" className="bg-primary text-primary-foreground font-semibold px-2 py-0.5 rounded-sm text-[10px] uppercase">
                        {service.badge}
                      </Badge>}
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base text-muted-foreground leading-relaxed">
                    {service.description}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-2 mt-auto">
                  {service.link.startsWith("http") ? <Link href={service.link} className="group/link inline-flex items-center text-sm font-bold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors">
                      Learn More
                      <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link> : <Link to={service.link} className="group/link inline-flex items-center text-sm font-bold uppercase tracking-wider text-primary hover:text-primary/80 transition-colors">
                      Learn More
                      <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover/link:translate-x-1" />
                    </Link>}
                </CardFooter>
              </Card>
            </div>)}
        </div>
      </div>
    </section>;
}